import { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { prefix } from "../api";
import { store } from "../store";

const STEP_LABEL = {
  a1: "A1 Invite reminder",
  a2: "A2 Welcome",
  a3: "A3 Upload nudge",
  a3b: "A3b Uploaded, no run",
  a4: "A4 Export nudge",
  a5: "A5 After export",
  a6: "A6 More SKUs",
  a7: "A7 Still no export",
};

const VERDICT_LABEL = {
  stale: "Skipped",
  due: "Due",
  waitingForGap: "Waiting",
  notYet: "Not yet",
  notEligible: "Does not apply",
  alreadySent: "Already sent",
};

const VERDICT_COLOR = {
  stale: "orange",
  due: "green",
  waitingForGap: "yellow",
  notYet: "blue",
  notEligible: "gray",
  alreadySent: "gray",
};

function when(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function factLine(facts) {
  if (!facts) return "";
  return [
    facts.activatedAt ? `Signed in ${when(facts.activatedAt)}` : "Has not signed in",
    facts.firstUploadAt ? "Uploaded" : "No upload",
    facts.firstRunStartedAt ? "Run started" : "No run",
    facts.firstRunCompletedAt ? "Run completed" : "No completed run",
    facts.firstExportAt ? "Exported" : "No export recorded",
    `${facts.allocatedSkusRemaining ?? 0} sponsored SKUs left`,
  ].join(" · ");
}

async function dripFetch(method, uri, { missingOk = false, body } = {}) {
  const authToken = store.getState().authToken;
  const response = await fetch(`${prefix}/${uri}`, {
    method,
    headers: {
      "content-type": "application/json",
      accept: "application/json",
      ...(authToken ? { authorization: `Bearer ${authToken}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (response.status === 404 && missingOk) return null;
  const text = await response.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!response.ok) {
    throw new Error(json?.message || json?.detail || text || response.statusText);
  }
  return json;
}

export default function TenantDrips({ tenantId }) {
  const [status, setStatus] = useState(undefined);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const loadGen = useRef(0);
  const tenantRef = useRef(tenantId);
  tenantRef.current = tenantId;

  const load = useCallback(async () => {
    const gen = ++loadGen.current;
    const requested = tenantId;
    setError(null);
    try {
      const [next, config] = await Promise.all([
        dripFetch("GET", `admin/onboarding-drips/merchants/${requested}`, { missingOk: true }),
        dripFetch("GET", "admin/onboarding-drips/settings"),
      ]);
      if (gen !== loadGen.current || tenantRef.current !== requested) return;
      setStatus(next);
      setSettings(config);
    } catch (err) {
      if (gen !== loadGen.current || tenantRef.current !== requested) return;
      setError(err.message || "Could not load onboarding drips.");
    }
  }, [tenantId]);

  useEffect(() => {
    setStatus(undefined);
    setPreview(null);
    setSettings(null);
    load();
  }, [load]);

  const previewStep = async (step) => {
    const requested = tenantId;
    setBusy(true);
    try {
      const email = await dripFetch(
        "GET",
        `admin/onboarding-drips/merchants/${requested}/emails/${step}/preview`,
      );
      if (tenantRef.current !== requested) return;
      setPreview(email);
    } catch (err) {
      if (tenantRef.current !== requested) return;
      notifications.show({ color: "red", title: "Preview failed", message: err.message });
    } finally {
      if (tenantRef.current === requested) setBusy(false);
    }
  };

  const sendStep = async (step, label) => {
    const requested = tenantId;
    const email = status.email;
    const bcc = settings?.bcc?.trim();
    const copy = bcc ? ` A copy goes to ${bcc}.` : "";
    const ok = window.confirm(
      `Send ${label} to ${email} now? This is a real email.${copy}`,
    );
    if (!ok) return;
    setBusy(true);
    try {
      const next = await dripFetch(
        "POST",
        `admin/onboarding-drips/merchants/${requested}/emails/${step}/send`,
      );
      if (tenantRef.current !== requested) return;
      if (!next) throw new Error("This tenant is not in the onboarding drip cohort.");
      setStatus(next);
      notifications.show({ color: "green", title: "Email sent", message: label });
    } catch (err) {
      if (tenantRef.current !== requested) return;
      notifications.show({ color: "red", title: "Email not sent", message: err.message });
      await load();
    } finally {
      if (tenantRef.current === requested) setBusy(false);
    }
  };

  return (
    <Paper my="md" withBorder p="md">
      <Title order={4}>Onboarding emails</Title>
      {status === undefined && !error && (
        <Group mt="md">
          <Loader size="sm" />
          <Text size="sm">Loading drip status…</Text>
        </Group>
      )}
      {error && (
        <Stack mt="md" gap="xs">
          <Text size="sm" c="red">{error}</Text>
          <Button variant="default" onClick={load}>Try again</Button>
        </Stack>
      )}
      {status === null && (
        <Text size="sm" c="dimmed" mt="md">
          This tenant is not in the onboarding drip cohort.
        </Text>
      )}
      {status && (
        <Stack mt="md" gap="sm">
          <Text size="sm">{status.email}</Text>
          <Text size="sm" c="dimmed">{factLine(status.facts)}</Text>
          {status.mode === "dryRun" && (
            <Text size="sm">
              The scheduler is in dry-run and sends nothing on its own. Send now delivers a real email.
            </Text>
          )}
          {settings?.bcc?.trim() && (
            <Text size="sm">A copy of each live email goes to {settings.bcc.trim()}.</Text>
          )}
          {status.halt && (
            <Text size="sm" c="red">Stopped: {status.halt}</Text>
          )}
          {(status.steps || []).map((step) => {
            const label = STEP_LABEL[step.step] || step.step;
            const unconfirmed = step.outcome === "claimed";
            const canSend = !status.halt && !unconfirmed &&
              (step.verdict === "stale" || step.verdict === "due");
            return (
              <Group key={step.step} justify="space-between" align="flex-start">
                <div>
                  <Group gap="xs">
                    <Text size="sm" fw={500}>{label}</Text>
                    <Badge color={unconfirmed ? "yellow" : (VERDICT_COLOR[step.verdict] || "gray")} variant="light">
                      {unconfirmed ? "Not confirmed" : (VERDICT_LABEL[step.verdict] || step.verdict)}
                    </Badge>
                  </Group>
                  <Text size="xs" c="dimmed">
                    {step.reason}
                    {step.dueAt ? ` · due ${when(step.dueAt)}` : ""}
                  </Text>
                </div>
                <Group gap="xs" wrap="nowrap">
                  <Button size="xs" variant="default" disabled={busy} onClick={() => previewStep(step.step)}>
                    Preview
                  </Button>
                  {canSend && (
                    <Button size="xs" disabled={busy} onClick={() => sendStep(step.step, label)}>
                      Send now
                    </Button>
                  )}
                </Group>
              </Group>
            );
          })}
          {status.sends?.length > 0 && (
            <Stack gap={4} mt="xs">
              <Text size="sm" fw={500}>Log</Text>
              {[...status.sends].reverse().map((row) => (
                <Text key={row.id} size="xs" c="dimmed">
                  {STEP_LABEL[row.step] || row.step} · {row.mode} · {row.status === "claimed" ? "not confirmed" : row.status}
                  {row.completedAt || row.claimedAt
                    ? ` · ${when(row.completedAt || row.claimedAt)}`
                    : ""}
                  {row.subject ? ` · ${row.subject}` : ""}
                  {row.error ? ` · ${row.error}` : ""}
                </Text>
              ))}
            </Stack>
          )}
        </Stack>
      )}
      <Modal
        opened={preview != null}
        onClose={() => setPreview(null)}
        title={preview?.subject || "Preview"}
        size="lg"
      >
        <Text size="sm" c="dimmed" mb="sm">
          To {preview?.to}. This preview does not send.
          {preview?.text?.includes("FRESH-TOKEN")
            ? " The invite link is a placeholder. Send now mints a fresh link."
            : ""}
        </Text>
        <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>{preview?.text}</Text>
      </Modal>
    </Paper>
  );
}
